import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../ui/cn';
import Button from '../ui/Button';
import type { MegaGroup } from './MegaMenu';
import { ChevronIcon } from './Icons';
import { mainLinks, siteInfo } from './siteInfo';

function Fold({ label, groups, footer }: { label: string; groups: MegaGroup[]; footer?: { label: string; to: string } }) {
  const [open, setOpen] = useState(false);
  const [sub, setSub] = useState<string | null>(null);
  return (
    <li className="border-b border-line">
      <button aria-expanded={open} onClick={() => setOpen(!open)} className="flex w-full items-center justify-between py-4 font-display text-2xl font-semibold">
        {label}
        <ChevronIcon className={cn('h-2.5 w-4 transition-transform duration-300', open && 'rotate-180')} />
      </button>
      <div className={cn('grid transition-[grid-template-rows] duration-300', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
        <ul className="overflow-hidden">
          {groups.map((g) => (
            <li key={g.id}>
              {g.links.length === 0 && g.cta ? (
                <Link to={g.cta.to} className="block py-3 pl-4 text-lg text-ink">{g.label}</Link>
              ) : (
              <button aria-expanded={sub === g.id} onClick={() => setSub(sub === g.id ? null : g.id)} className="flex w-full items-center justify-between py-3 pl-4 text-left text-lg text-ink">
                {g.label}
                <ChevronIcon className={cn('h-2 w-3 shrink-0 transition-transform', sub === g.id && 'rotate-180')} />
              </button>
              )}
              {sub === g.id && g.links.length > 0 && (
                <ul className="mb-2 border-l-2 border-brand pl-4 ml-4">
                  {g.cta && <li><Link to={g.cta.to} className="block py-2 text-sm font-semibold uppercase tracking-[0.094rem] text-brand-dark">{g.cta.label}</Link></li>}
                  {g.links.map((l) => <li key={l.to}><Link to={l.to} className="block py-2 text-base">{l.label}</Link></li>)}
                </ul>
              )}
            </li>
          ))}
          {footer && <li><Link to={footer.to} className="block py-3 pl-4 text-sm font-semibold uppercase tracking-[0.094rem] text-brand-dark">{footer.label}</Link></li>}
        </ul>
      </div>
    </li>
  );
}

export default function MobileMenu({ open, onClose, sectorGroups, serviceGroups }: { open: boolean; onClose: () => void; sectorGroups: MegaGroup[]; serviceGroups: MegaGroup[] }) {
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <div aria-hidden={!open} className={cn('fixed inset-0 z-[60] flex flex-col bg-white transition-transform duration-300 ease-out lg:hidden', open ? 'translate-x-0' : 'pointer-events-none translate-x-full')}>
      <div className="flex h-[5.5rem] shrink-0 items-center justify-between px-6 md:px-8">
        <Link to="/" onClick={onClose}><img src="/logo.png" alt="Flex Group" className="h-12 w-auto" /></Link>
        <button aria-label="Fermer le menu" onClick={onClose} className="flex h-11 w-11 items-center justify-center">
          <svg viewBox="0 0 24 24" className="h-6 w-6" stroke="currentColor" strokeWidth="2" fill="none"><path d="M5 5l14 14M19 5L5 19" /></svg>
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto px-6 pb-10 md:px-8" aria-label="Menu mobile">
        <ul>
          <Fold label="Secteurs" groups={sectorGroups} />
          <Fold label="Services" groups={serviceGroups} footer={{ label: 'Voir tous les services', to: '/services' }} />
          {mainLinks.map((l) => (
            <li key={l.to} className="border-b border-line"><Link to={l.to} onClick={onClose} className="block py-4 font-display text-2xl font-semibold">{l.label}</Link></li>
          ))}
        </ul>
        <div className="mt-8"><Button to="/contact" className="w-full" onClick={onClose}>Contact</Button></div>
        <div className="mt-8 space-y-1 text-sm text-ink">
          <a href={siteInfo.phoneHref} className="block">{siteInfo.phone}</a>
          <a href={`mailto:${siteInfo.email}`} className="block">{siteInfo.email}</a>
        </div>
      </nav>
    </div>
  );
}
